import { useState } from "react";
import type { ReviewTask } from "../api/changes";
import { TASK_REVIEW } from "../copy/changes";
import { Button } from "../ui";

export function TaskReview({
  tasks: initial,
  scenes,
  intent: initialIntent = "",
  busy,
  onConfirm,
  onCancel,
}: {
  tasks: ReviewTask[];
  scenes: Record<string, string>;
  intent?: string;
  busy: boolean;
  onConfirm(
    tasks: ReviewTask[],
    interpretation: { intent: string; assumptions: string[] },
  ): void;
  onCancel(): void;
}) {
  const [intent, setIntent] = useState(initialIntent);
  const [editingStory, setEditingStory] = useState(false);
  const [tasks, setTasks] = useState(() =>
    initial.map((t) => ({ ...t, scenes: [...t.scenes] })),
  );
  const update = (index: number, value: Partial<ReviewTask>) =>
    setTasks((items) =>
      items.map((item, i) => (i === index ? { ...item, ...value } : item)),
    );
  const valid =
    tasks.length > 0 &&
    tasks.every((t) => t.text.trim() && t.scenes.length) &&
    new Set(tasks.map((t) => t.text.trim())).size === tasks.length;
  return (
    <section
      aria-label={TASK_REVIEW.title}
      data-testid="task-review"
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-brand-light bg-brand-tint/30 p-3"
    >
      {editingStory ? (
        <textarea
          aria-label={TASK_REVIEW.story}
          className="tb-input min-h-24 w-full resize-y"
          rows={4}
          value={intent}
          maxLength={320}
          disabled={busy}
          onChange={(e) => setIntent(e.target.value)}
        />
      ) : intent ? (
        <p className="m-0 break-words text-sm leading-relaxed">{intent}</p>
      ) : null}
      <Button
        size="sm"
        variant="ghost"
        className="self-start"
        disabled={busy}
        onClick={() => setEditingStory((editing) => !editing)}
      >
        {editingStory ? TASK_REVIEW.done : TASK_REVIEW.editStory}
      </Button>
      <ol className="m-0 flex list-none flex-col gap-3 p-0">
        {tasks.map((task, i) => (
          <li
            key={i}
            className="flex min-w-0 flex-col gap-2 rounded border border-slate-200 bg-white p-2"
          >
            <label
              className="text-sm font-semibold"
              htmlFor={`review-task-${i}`}
            >
              {TASK_REVIEW.task} {i + 1}
            </label>
            <textarea
              id={`review-task-${i}`}
              rows={4}
              className="tb-input min-h-16 w-full resize-y"
              value={task.text}
              maxLength={500}
              disabled={busy}
              onChange={(e) => update(i, { text: e.target.value })}
            />
            <details className="text-sm">
              <summary className="cursor-pointer break-words">
                {TASK_REVIEW.scenes}:{" "}
                {task.scenes.map((s) => scenes[s] ?? s).join(", ")}
              </summary>
              <fieldset
                className="m-0 flex flex-col gap-1 border-0 p-1"
                disabled={busy}
                aria-label={`${TASK_REVIEW.scenes} ${i + 1}`}
              >
                {Object.entries(scenes).map(([id, name]) => (
                  <label key={id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={task.scenes.includes(id)}
                      onChange={(e) =>
                        update(i, {
                          scenes: e.target.checked
                            ? [...task.scenes, id]
                            : task.scenes.filter((s) => s !== id),
                        })
                      }
                    />
                    {name}
                  </label>
                ))}
              </fieldset>
            </details>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() =>
                setTasks((items) => items.filter((_, k) => k !== i))
              }
              aria-label={`${TASK_REVIEW.remove} ${i + 1}`}
              className="self-start"
            >
              {TASK_REVIEW.remove}
            </Button>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || tasks.length >= 24}
          onClick={() =>
            setTasks((items) => [
              ...items,
              {
                text: "",
                scenes: initial[0]?.scenes ?? Object.keys(scenes).slice(0, 1),
              },
            ])
          }
        >
          {TASK_REVIEW.add}
        </Button>
        <Button
          size="sm"
          disabled={busy || !valid}
          onClick={() =>
            onConfirm(
              tasks.map((t) => ({ ...t, text: t.text.trim() })),
              { intent: intent.trim(), assumptions: [] },
            )
          }
        >
          {TASK_REVIEW.confirm}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          {TASK_REVIEW.cancel}
        </Button>
      </div>
    </section>
  );
}
